/**
 * Personal settings React Query hooks.
 *
 * Covers the three-scope settings bundle (system -> tenant -> user) and
 * the definitions catalog used to render the settings UI. Keys live
 * under ['settings', ...].
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/hooks/useAppApi'
import type { SettingDefinition } from '@shared/settings/definitions'

export interface SettingsBundleResponse {
  effective: Array<{
    key: string
    value: unknown
    source: 'user' | 'tenant' | 'system' | 'default'
    visibility: 'public' | 'private'
  }>
  raw: Array<{
    scope: 'system' | 'tenant' | 'user'
    key: string
    value: unknown
    updatedAt: string
  }>
}

export function useSettingsBundle(enabled = true) {
  const api = useApi()
  return useQuery<SettingsBundleResponse>({
    queryKey: ['settings', 'bundle'],
    queryFn: () => api.get('/settings/bundle') as Promise<SettingsBundleResponse>,
    enabled,
  })
}

export function useSettingDefinitions() {
  const api = useApi()
  return useQuery<SettingDefinition[]>({
    queryKey: ['settings', 'definitions'],
    queryFn: () => api.get('/settings/definitions') as Promise<SettingDefinition[]>,
  })
}

export function useSettingMutations() {
  const api = useApi()
  const qc = useQueryClient()
  return {
    setUser: useMutation({
      mutationFn: ({ key, value }: { key: string; value: unknown }) =>
        api.put(`/settings/user/${encodeURIComponent(key)}`, { value }),
      onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
    }),
    setTenant: useMutation({
      mutationFn: ({ key, value }: { key: string; value: unknown }) =>
        api.put(`/settings/tenant/${encodeURIComponent(key)}`, { value }),
      onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
    }),
    setSystem: useMutation({
      mutationFn: ({ key, value }: { key: string; value: unknown }) =>
        api.put(`/admin/settings/system/${encodeURIComponent(key)}`, { value }),
      onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
    }),
  }
}
